import {
  useAddBreak,
  useDeleteBreak,
  useUpdateBreak,
} from '@features/time-logging/hooks/useBreaks'
import { useUpdateTimeCard } from '@features/time-logging/hooks/useTimeCardManagement'
import { useTimeCards } from '@hooks/useTimeCards'
import { padDigit } from '@utils/padDigit'
import { range as generateRange } from '@utils/range'
import { col } from '@utils/styles'
import { FC, useEffect, useMemo, useState, useCallback } from 'react'
import { Section } from '../section'
import GridColumn from '../table/GridColumn'
import {
  Table,
  SideColumn,
  LeftHeader,
  SummaryCell,
  WorkDayCell,
  SummaryRow,
} from 'components/time-entry-table'
import { BreakControl } from './components/BreakControl'
import { sortIntervals, filterOutUndefinedIntervals } from 'utils/intervals'
import { CardColumn } from './types'
import { formatTime, formatDuration } from './utils/format'
import { useSettings } from '@hooks/useSettings'
import { totalWorkedInSeconds } from '@utils/timeCards'
import { isNextDay } from '@utils/time'
import { useUserId } from '@features/time-logging/hooks/useUserId'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { useUpdateCorrection } from '@features/time-logging/hooks/useCorrections'
import { SectionHeading } from '@features/time-logging/components'
import { useWeekScope } from '@hooks/useWeekScope'
import { useDateTimeToLocaleString } from '@hooks/useDateTimeWithLocale'

interface Props {
  onCellHover: (date: DateTime | null) => void
  readOnly: boolean
}

export const WorkDayTable: FC<Props> = ({ onCellHover, readOnly }) => {
  const { t } = useTranslation()
  const { end, range, start, weekStartsOn } = useWeekScope()
  const [rowHovered, setRowHovered] = useState(0)
  const [numOfBreakRows, setNumOfBreakRows] = useState(1)
  const settingsQueryResult = useSettings()

  const clock = settingsQueryResult.data?.timeFormat === '24_hour' ? '24' : '12'

  const userId = useUserId()
  const timeCardsQuery = useTimeCards({ userId, start, end })
  const timeCardsLoading = useMemo(
    () => timeCardsQuery.data === undefined,
    [timeCardsQuery],
  )
  const timeCards = useMemo(
    () => timeCardsQuery.data ?? [],
    [timeCardsQuery.data],
  )

  const timeCardMutation = useUpdateTimeCard({ start, end })
  const correctedTimeCardMutation = useUpdateCorrection({ start, end })
  const breakMutation = useUpdateBreak({ start, end })
  const addBreakMutation = useAddBreak({ start, end })
  const deleteBreakMutation = useDeleteBreak({ start, end })

  const cardColumns: CardColumn[] = useMemo(
    () =>
      range.map((date) => {
        const card = timeCards?.find((card) => card.date.hasSame(date, 'day'))

        return card
          ? {
              id: card.id,
              date,
              startAt: card.startAt,
              endAt: card.endAt,
              breaks: sortIntervals(filterOutUndefinedIntervals(card.breaks)),
              total: totalWorkedInSeconds(card),
              timeZone: card.timeZone,
              submitted: card.submitted,
              correction: card.correction,
              capturesMealBreaks: card.capturesMealBreaks,
              minTime: card.minTime,
              maxTime: card.maxTime,
            }
          : {
              id: 0,
              date,
              startAt: null,
              endAt: null,
              breaks: [],
              total: 0,
              timeZone: 'America/Los_Angeles', // App's Pacific default; matches WorkWeekTable
              submitted: true,
              correction: false,
              capturesMealBreaks: false,

              // This shouldn't matter, but set null column min/max times to midnight -> midnight
              minTime: date.startOf('day'),
              maxTime: date.plus({ day: 1 }).startOf('day'),
            }
      }),
    [range, timeCards],
  )

  useEffect(() => {
    const maxNumberOfBreaksPerCard = cardColumns.reduce(
      (maxBreaks, cardColumn) => {
        const numOfBreaksWithinCard = cardColumn.breaks.length ?? 0
        return maxBreaks < numOfBreaksWithinCard
          ? numOfBreaksWithinCard
          : maxBreaks
      },
      1,
    )

    // If the max number of breaks on any given day exceeds the current
    // number of displayed breaks, display enough breaks to acommodate.
    // Note that we should never reduce numOfBreakRows to match maxNumberOfBreaksPerCard,
    // as the user may have intentionally added a break row and it may just
    // currently not have any breaks because they are in the process of adding breaks
    // to time cards.
    if (maxNumberOfBreaksPerCard > numOfBreakRows) {
      setNumOfBreakRows(maxNumberOfBreaksPerCard)
    }
  }, [cardColumns, numOfBreakRows])

  const breakNumbers = generateRange(1, numOfBreakRows)

  const numOfRows = numOfBreakRows + 2 // start and end

  const handleTimeSubmit = (
    cardColumn: CardColumn,
    startAtSecondsSinceMidnight: number | null,
    endAtSecondsSinceMidnight: number | null,
  ) => {
    if (!cardColumn.id) return

    if (cardColumn.correction) {
      correctedTimeCardMutation.mutate({
        id: cardColumn.id,
        startAt: startAtSecondsSinceMidnight,
        endAt: endAtSecondsSinceMidnight,
      })
    } else {
      timeCardMutation.mutate({
        id: cardColumn.id,
        startAt: startAtSecondsSinceMidnight,
        endAt: endAtSecondsSinceMidnight,
      })
    }
  }

  const handleBreakSubmit = (
    breakId: number | undefined,
    cardId: number | undefined,
    startAtSecondsSinceMidnight: number | null,
    endAtSecondsSinceMidnight: number | null,
    isMeal: boolean,
  ) => {
    if (cardId === undefined) return

    const startAt = startAtSecondsSinceMidnight
    const endAt = endAtSecondsSinceMidnight

    if (breakId === undefined && startAt === null && endAt === null) return
    if (breakId === undefined)
      return addBreakMutation.mutate({
        timeCardId: cardId,
        startAt,
        endAt,
        isMeal,
      })
    if (startAt === null && endAt === null)
      return deleteBreakMutation.mutate(breakId)

    breakMutation.mutate({ breakId, startAt, endAt, isMeal })
  }

  const cardColumnIsEditable = useCallback(
    (card: CardColumn): boolean => {
      if (card.id === undefined || card.id === 0) return false

      return !readOnly && card.date <= DateTime.now() && !card.submitted
    },
    [readOnly],
  )

  const localizeShortDate = useDateTimeToLocaleString()({
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })

  return (
    <Section loading={timeCardsLoading} testId="work-day">
      <SectionHeading>{t('features.timeLogging.workDay')}</SectionHeading>
      <Table weekStartsOn={weekStartsOn}>
        <SideColumn side="left">
          <LeftHeader
            highlight={rowHovered === 1}
            labels={[t('features.timeLogging.start')]}
            rowIndex={1}
          />
          {breakNumbers.map((breakNumber) => (
            <LeftHeader
              highlight={rowHovered == breakNumber + 1}
              key={`break-${breakNumber}`}
              labels={[t('features.timeLogging.break'), padDigit(breakNumber)]}
              rowIndex={breakNumber + 1}
            >
              <BreakControl
                breakLabel={padDigit(breakNumber)}
                onBreakAdd={() => setNumOfBreakRows((num) => num + 1)}
                onBreakRemove={() => setNumOfBreakRows((num) => num - 1)}
                removeDisabled={cardColumns.some((card) => {
                  if (breakNumber === 1) return true

                  return card.breaks.at(breakNumber - 1)
                })}
                addDisabled={cardColumns.every(
                  (card) => card.breaks.at(breakNumber - 1) === undefined,
                )}
              />
            </LeftHeader>
          ))}
          <LeftHeader
            highlight={rowHovered === numOfRows}
            labels={[t('features.timeLogging.end')]}
            rowIndex={numOfRows}
          />
        </SideColumn>
        {cardColumns.map((cardColumn) => (
          <GridColumn
            date={cardColumn.date}
            key={`work-day-column-${cardColumn.date.toString()}`}
          >
            <WorkDayCell
              rowIndex={0}
              date={cardColumn.date}
              editable={cardColumnIsEditable(cardColumn)}
              onHoverOver={() => {
                onCellHover(cardColumn.date)
                setRowHovered(1)
              }}
              onHoverLeave={() => {
                onCellHover(null)
                setRowHovered(0)
              }}
              formattedContents={formatTime(
                cardColumn.startAt,
                cardColumn.date,
                cardColumn.timeZone,
                { clock },
              )}
              variant="time-card"
              nextDay={
                cardColumn.startAt !== null &&
                isNextDay(cardColumn.startAt, cardColumn.date, cardColumn.timeZone)
              }
              startAtSecondsSinceMidnight={cardColumn.startAt}
              endAtSecondsSinceMidnight={cardColumn.endAt}
              onSubmit={(
                startAtSecondsSinceMidnight: number | null,
                endAtSecondsSinceMidnight: number | null,
              ) => {
                handleTimeSubmit(
                  cardColumn,
                  startAtSecondsSinceMidnight,
                  endAtSecondsSinceMidnight,
                )
              }}
              autoFocusedTimeWidgetAdjuster="start"
              timeZone={cardColumn.timeZone}
              minTime={cardColumn.minTime}
              maxTime={cardColumn.maxTime}
              timeWidgetTitle={localizeShortDate(cardColumn.date)}
              testId="work-day-start"
            />
            {breakNumbers.map((breakNumber, breakNumberIndex) => {
              // Grabbing break based on break number since breaks are sorted
              const outTime = cardColumn.breaks.at(breakNumber - 1)

              return (
                <WorkDayCell
                  autoFocusedTimeWidgetAdjuster="start"
                  date={cardColumn.date}
                  editable={cardColumnIsEditable(cardColumn)}
                  endAtSecondsSinceMidnight={outTime?.endAt ?? null}
                  formattedContents={formatDuration(
                    outTime,
                    cardColumn.date,
                    cardColumn.timeZone,
                    { clock },
                  )}
                  key={`work-day-cell-break-${cardColumn.date.toString()}-${
                    outTime?.id ?? 0
                  }-${breakNumberIndex}`}
                  isMeal={Boolean(outTime?.isMeal)}
                  nextDay={
                    outTime !== undefined &&
                    isNextDay(outTime.endAt, cardColumn.date, cardColumn.timeZone)
                  }
                  isMealByDefault={
                    outTime === undefined &&
                    breakNumberIndex === 0 &&
                    cardColumn.breaks.every((b) => !b.isMeal)
                  }
                  onHoverLeave={() => {
                    onCellHover(null)
                    setRowHovered(0)
                  }}
                  onHoverOver={() => {
                    onCellHover(cardColumn.date)
                    setRowHovered(breakNumber + 1)
                  }}
                  rowIndex={breakNumber}
                  showIsMealCheckbox={cardColumn.capturesMealBreaks}
                  startAtSecondsSinceMidnight={outTime?.startAt ?? null}
                  onSubmit={(
                    startAtSecondsSinceMidnight: number | null,
                    endAtSecondsSinceMidnight: number | null,
                    isMeal: boolean,
                  ) =>
                    handleBreakSubmit(
                      outTime?.id,
                      cardColumn.id,
                      startAtSecondsSinceMidnight,
                      endAtSecondsSinceMidnight,
                      cardColumn.capturesMealBreaks && isMeal,
                    )
                  }
                  timeZone={cardColumn.timeZone}
                  minTime={cardColumn.minTime}
                  maxTime={cardColumn.maxTime}
                  timeWidgetTitle={localizeShortDate(cardColumn.date)}
                  variant="break"
                />
              )
            })}
            <WorkDayCell
              rowIndex={numOfRows - 1}
              date={cardColumn.date}
              editable={cardColumnIsEditable(cardColumn)}
              onHoverOver={() => {
                onCellHover(cardColumn.date)
                setRowHovered(numOfRows)
              }}
              onHoverLeave={() => {
                onCellHover(null)
                setRowHovered(0)
              }}
              formattedContents={formatTime(
                cardColumn.endAt,
                cardColumn.date,
                cardColumn.timeZone,
                { clock },
              )}
              variant="time-card"
              nextDay={
                cardColumn.endAt !== null &&
                isNextDay(cardColumn.endAt, cardColumn.date, cardColumn.timeZone)
              }
              startAtSecondsSinceMidnight={cardColumn.startAt}
              endAtSecondsSinceMidnight={cardColumn.endAt}
              onSubmit={(
                startAtSecondsSinceMidnight: number | null,
                endAtSecondsSinceMidnight: number | null,
              ) =>
                handleTimeSubmit(
                  cardColumn,
                  startAtSecondsSinceMidnight,
                  endAtSecondsSinceMidnight,
                )
              }
              autoFocusedTimeWidgetAdjuster="end"
              timeZone={cardColumn.timeZone}
              minTime={cardColumn.minTime}
              maxTime={cardColumn.maxTime}
              timeWidgetTitle={localizeShortDate(cardColumn.date)}
              testId="work-day-end"
            />
          </GridColumn>
        ))}
      </Table>
      <SummaryRow testId="work-day-summary" weekStartsOn={weekStartsOn}>
        {cardColumns.map((card, index) => (
          <SummaryCell
            key={`work-day-summary-${card.date.toString()}`}
            gridClass={col(card.date)}
            total={card.total}
            skipBorder={index === cardColumns.length - 1}
          />
        ))}
      </SummaryRow>
    </Section>
  )
}
